// Circle Playlist — a shared playlist for a Circle (group conversation), stored as a
// CIRCLE-scoped playlist (provider-neutral external refs). Members see it; managers curate
// (create + add tracks — reuses recognition:manage on the playlist endpoints). Play queues
// the streamable tracks through the KaziPeople player. No listening data.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Music, Play, Plus, ExternalLink, ListMusic, Loader2, Search, Volume2 } from 'lucide-react';
import { Input } from '@/features/shared/components/ui/input';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import { openProviderUrl, isSafeProviderUrl } from '@/lib/providerLinks';
import { usePlayer } from '@/features/music/player/usePlayer';
import { appleRef, resolveSongs } from '@/features/music/lib/playableRef';
import { appleMusicProvider } from '@/features/music/lib/appleMusicProvider';
import { useMusicConnected } from '@/features/music/lib/useMusicConnected';
import { useHasPermission } from '@/rbac/useRbac';
import type { MusicItem } from '@/features/music/lib/types';
import * as api from '@/services/performanceService';

export const CirclePlaylist: React.FC<{ conversationId: string; circleName: string }> = ({ conversationId, circleName }) => {
  const { snap, setQueue, playItem } = usePlayer();
  const canManage = useHasPermission('recognition:manage');
  const [playlist, setPlaylist] = useState<api.MusicPlaylist | null>(null);
  const [items, setItems] = useState<api.MusicPlaylistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  // Catalog search (needs the personal Apple Music connection for the catalog API);
  // the paste-a-link box below stays as the provider-neutral fallback.
  const { sessionConnected } = useMusicConnected();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<MusicItem[]>([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const term = q.trim();
    if (!sessionConnected || term.length < 2) { setResults([]); return; }
    let live = true;
    setSearching(true);
    const h = setTimeout(() => {
      appleMusicProvider().searchCatalog(term, ['songs'], { limit: 8 })
        .then((r) => { if (live) setResults(r.songs); })
        .catch(() => { if (live) setResults([]); })
        .finally(() => { if (live) setSearching(false); });
    }, 300);
    return () => { live = false; clearTimeout(h); };
  }, [q, sessionConnected]);

  const load = () => api.listMusicPlaylists('CIRCLE', conversationId)
    .then(async (pls) => {
      const p = pls[0] || null;
      setPlaylist(p);
      if (p) { const d = await api.getMusicPlaylist(p.id); setItems(d.items || []); }
      else setItems([]);
    })
    .catch(() => { setPlaylist(null); setItems([]); })
    .finally(() => setLoading(false));

  useEffect(() => { setLoading(true); void load(); /* eslint-disable-next-line */ }, [conversationId]);

  const create = async () => {
    setBusy(true);
    try {
      const p = await api.createMusicPlaylist({ name: `${circleName} Mix`, scope_type: 'CIRCLE', scope_id: conversationId });
      setPlaylist(p); setItems([]);
      toast.success('Circle playlist created 🎵');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not create the playlist'));
    } finally { setBusy(false); }
  };
  const addTrack = async (u: string, title?: string, artist?: string) => {
    if (!playlist) return;
    if (!isSafeProviderUrl(u)) { toast.error('Paste an Apple Music, Spotify, or YouTube Music link.'); return; }
    setBusy(true);
    try { await api.addMusicPlaylistItem(playlist.id, { url: u, title, artist }); await load(); setUrl(''); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not add that track')); }
    finally { setBusy(false); }
  };
  // Play stays INSIDE KaziPeople: every row is resolved to catalog songs (albums and
  // playlists are expanded) and queued in order, so the whole list plays through. It never
  // hands the listener off to the provider's website — the ↗ button on a row does that.
  const [starting, setStarting] = useState(false);
  const play = async () => {
    setStarting(true);
    try {
      const mk = appleMusicProvider();
      // Album and playlist links expand through the public catalog, which works with the
      // app's own developer token — so the queue is built even before a listener connects.
      const { songs, partial } = await resolveSongs(items, { album: (id) => mk.getAlbum(id), playlist: (id) => mk.getPlaylist(id) });
      if (!songs.length) {
        toast.error('Nothing in this playlist can be played here yet.');
        return;
      }
      if (!sessionConnected) {
        toast.error('Connect Apple Music on the Music page to listen here.');
        return;
      }
      await setQueue(songs);
      if (partial) toast('Playing the tracks we could load from this playlist.', { icon: '🎵' });
    } finally {
      setStarting(false);
    }
  };

  if (loading) return <div className="py-3 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" /></div>;

  if (!playlist) {
    return (
      <div className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
        <div className="mb-2 flex items-center gap-2 font-medium text-foreground"><Music className="h-4 w-4 text-primary-500" />Circle Playlist</div>
        {canManage ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={create}><Plus className="mr-1.5 h-4 w-4" />Create playlist</Button>
        ) : <span>No circle playlist yet.</span>}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground"><Music className="h-4 w-4 text-primary-500" />{playlist.name}</div>
        <Button size="sm" disabled={items.length === 0 || starting} onClick={() => void play()}>
          {starting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}Play
        </Button>
      </div>
      <ul className="mb-2 space-y-0.5">
        {items.length === 0 && <li className="text-xs text-muted-foreground">No songs yet.</li>}
        {items.map((t) => {
          // The row itself plays, here in KaziPeople — a song starts, an album or playlist
          // link starts that album/playlist. ↗ is the only way out to the provider.
          const ref = appleRef(t);
          const nowPlaying = !!ref && snap.item?.id === ref.id;
          return (
            <li key={t.id} className="flex items-center gap-2 rounded hover:bg-muted">
              <button
                type="button"
                disabled={!ref}
                onClick={() => ref && void playItem({ kind: ref.kind, id: ref.id, provider: 'apple_music', title: t.title || '' })}
                aria-label={ref ? `Play ${t.title || 'track'}` : undefined}
                title={ref ? `Play ${t.title || 'track'}` : 'This link can only be opened with its provider'}
                className="flex min-w-0 flex-1 items-center gap-2 rounded p-1.5 text-left disabled:cursor-default"
              >
                {nowPlaying
                  ? <Volume2 className="h-3.5 w-3.5 shrink-0 text-primary-500" />
                  : <ListMusic className={`h-3.5 w-3.5 shrink-0 ${ref ? 'text-muted-foreground' : 'text-muted-foreground/50'}`} />}
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-xs font-medium ${nowPlaying ? 'text-primary-500' : 'text-foreground'}`} dir="auto">{t.title || t.canonical_url}</span>
                  {t.artist && <span className="block truncate text-[11px] text-muted-foreground" dir="auto">{t.artist}</span>}
                </span>
              </button>
              <button type="button" aria-label="Open" title="Open" onClick={() => openProviderUrl(t.canonical_url)} className="mr-1.5 rounded p-1 text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></button>
            </li>
          );
        })}
      </ul>
      {canManage && (
        <div className="space-y-1.5 border-t border-border pt-2">
          {snap.item?.canonicalUrl && (
            <Button size="sm" variant="outline" disabled={busy} className="w-full justify-start"
              onClick={() => addTrack(snap.item!.canonicalUrl!, snap.item!.title, snap.item!.artist)}>
              <Plus className="mr-1.5 h-4 w-4" />Add now playing: {snap.item.title}
            </Button>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={sessionConnected ? 'Search songs to add' : 'Connect Apple Music (Music page) to search'} disabled={!sessionConnected} className="h-8 pl-7 text-xs" />
          </div>
          {(searching || results.length > 0) && (
            <ul className="max-h-48 space-y-0.5 overflow-y-auto rounded-md border border-border p-1">
              {searching && results.length === 0 && <li className="p-1.5 text-xs text-muted-foreground">Searching…</li>}
              {results.map((s) => {
                const already = !!s.canonicalUrl && items.some((t) => t.canonical_url === s.canonicalUrl);
                return (
                  <li key={s.id} className="flex items-center gap-2 rounded p-1.5 hover:bg-muted">
                    {(() => { const art = appleMusicProvider().artworkUrl(s.artwork, 56); return art ? <img src={art} alt="" className="h-7 w-7 shrink-0 rounded object-cover" /> : <ListMusic className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />; })()}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-foreground" dir="auto">{s.title}</div>
                      {s.artist && <div className="truncate text-[11px] text-muted-foreground" dir="auto">{s.artist}</div>}
                    </div>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy || already || !s.canonicalUrl}
                      onClick={() => s.canonicalUrl && addTrack(s.canonicalUrl, s.title, s.artist).then(() => { setQ(''); setResults([]); })}>
                      {already ? 'Added' : 'Add'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="flex gap-1.5">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Or paste a song link" className="h-8 flex-1 text-xs" />
            <Button size="sm" disabled={busy || !isSafeProviderUrl(url.trim())} onClick={() => addTrack(url.trim())}>Add</Button>
          </div>
        </div>
      )}
    </div>
  );
};
